import { IQuiz, IQuizFilters, IQuizUserFilters } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { SorterResult } from 'antd/lib/table/interface';
import { create as createStore } from 'zustand';

import { sdk } from '@/services';

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 20;

const QuizService = sdk.getService('QuizService');
interface IStore {
  page: number;
  limit: number;
  search: string;
  filter?: IQuizUserFilters;
  sort?: SorterResult<IQuiz>;
  setSearch: (search: string) => void;
  setPageAndLimit: (page: number, limit: number) => void;
  setFilter: (filter: IQuizUserFilters) => void;
  setSort: (sort: SorterResult<IQuiz>) => void;
}

const store = createStore<IStore>(set => ({
  page: INITIAL_PAGE,
  limit: INITIAL_LIMIT,
  search: '',
  setSearch: (search: string) => set(state => ({ ...state, search, page: INITIAL_PAGE })),
  setPageAndLimit: (page: number, limit: number) => set(state => ({ ...state, page, limit })),
  setFilter: (filter: IQuizUserFilters) => set(state => ({ ...state, page: INITIAL_PAGE, filter })),
  setSort: (sort: SorterResult<IQuiz>) => set(state => ({ ...state, sort }))
}));

interface UseMyQuizzesListOptions {
  isParent?: boolean;
  hasParentBaseQuiz?: boolean;
}

export const useMyQuizzesList = (options?: UseMyQuizzesListOptions) => {
  const { page, limit, setPageAndLimit, search, setSearch, filter, setFilter, sort, setSort } =
    store();

  const mergedFilter: IQuizFilters = {
    ...filter,
    ...(options?.isParent !== undefined && { isParent: options.isParent }),
    ...(options?.hasParentBaseQuiz !== undefined && {
      hasParentBaseQuiz: options.hasParentBaseQuiz
    })
  };

  const queryKey = ['quizzes', `page-${page}`, `limit-${limit}`, search, mergedFilter, sort];

  const { isLoading, data } = useQuery({
    queryKey,
    queryFn: () => QuizService.getQuizzes(search, { page, limit }, mergedFilter, sort)
  });

  const pagination = data && {
    page: data.page,
    limit: data.limit,
    hasNextPage: data.hasNextPage,
    hasPrevPage: data.hasPrevPage,
    nextPage: data.nextPage,
    pagingCounter: data.pagingCounter,
    prevPage: data.prevPage,
    totalDocs: data.totalDocs,
    totalPages: data.totalPages
  };

  return {
    quizzes: data?.docs,
    isLoading,
    pagination,
    filter,
    setFilter,
    setPageAndLimit,
    setSearch,
    setSort
  };
};
